import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Image as ImageIcon, ShoppingBag, Sparkles, Package, Zap, Shield, Monitor } from 'lucide-react';

function HomePage({ onNavigate }) {
  const handleNavigate = (page) => {
    if (typeof onNavigate === 'function') onNavigate(page);
  };

  const tools = [
    {
      key: 'imageConverter',
      title: 'Image Converter',
      description: 'Convert between PNG, HEIC, WebP, AVIF, JPEG, GIF, BMP and more. Apply filters, remove backgrounds, crop, and batch-process entire folders.',
      icon: ImageIcon,
      color: 'indigo',
      features: ['9 stackable image filters', 'Per-side crop controls', 'Background removal', 'Batch folder conversion', 'Drag & drop upload']
    },
    {
      key: 'listingGenerator',
      title: 'Listing Generator',
      description: 'Generate optimized product listings with AI. Create compelling titles, descriptions, and bullet points for e-commerce platforms.',
      icon: ShoppingBag,
      color: 'purple',
      features: ['AI-powered descriptions', 'SEO-optimized titles', 'Multi-platform support', 'Bulk generation', 'Custom templates']
    },
    {
      key: 'popCultureImageGen',
      title: 'Pop Culture Image Gen',
      description: 'Create eye-catching pop culture inspired product images with AI. Perfect for social media marketing and creative content.',
      icon: Sparkles,
      color: 'cyan',
      features: ['AI image generation', 'Pop culture styles', 'Social media ready', 'High resolution output', 'Custom prompts']
    },
    {
      key: 'productImageGen',
      title: 'Product Image Gen',
      description: 'Generate professional product photography with AI. Create studio-quality images for your listings without a photoshoot.',
      icon: Package,
      color: 'emerald',
      features: ['AI product photography', 'Multiple backgrounds', 'Studio lighting effects', 'E-commerce optimized', 'Batch generation']
    },
  ];

  const colorMap = {
    indigo: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', border: 'hover:border-indigo-500/50', dot: 'bg-indigo-400', shadow: 'shadow-indigo-500/20' },
    purple: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'hover:border-purple-500/50', dot: 'bg-purple-400', shadow: 'shadow-purple-500/20' },
    cyan: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'hover:border-cyan-500/50', dot: 'bg-cyan-400', shadow: 'shadow-cyan-500/20' },
    emerald: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'hover:border-emerald-500/50', dot: 'bg-emerald-400', shadow: 'shadow-emerald-500/20' },
  };

  return (
    <>
      <Helmet>
        <title>JustAutomateIt — Free Online Image & Product Tools</title>
        <meta name="description" content="Online tools to convert images, generate product listings, and create AI-powered product photography. Supports PNG, HEIC, WebP, AVIF, JPEG. Sign in with Google to get started." />
        <meta property="og:title" content="JustAutomateIt — Free Online Image & Product Tools" />
        <meta property="og:description" content="Convert images, generate product listings, and create AI product photography. Free, private, browser-based tools." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://justautomateit.com" />
      </Helmet>

      <div className="flex-grow flex flex-col items-center px-4 py-8 bg-gradient-to-br from-gray-900 via-slate-900 to-black min-h-screen">

        {/* Hero */}
        <section className="w-full max-w-5xl text-center py-16 animate-fade-in">
          <h1 className="text-5xl sm:text-7xl font-black bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-cyan-400 mb-6 tracking-tight">
            JustAutomateIt
          </h1>
          <p className="text-xl sm:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed font-light">
            Powerful online tools for image conversion, product listing generation, and AI-powered product photography. Sign in with Google to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => handleNavigate('imageConverter')}
              className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold py-4 px-10 rounded-full shadow-lg shadow-indigo-500/30 transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-indigo-500/50"
            >
              Get Started
              <ArrowRight size={20} />
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 mt-8">
            <div className="flex items-center text-gray-400">
              <Zap size={16} className="mr-1.5 text-indigo-400" />
              <span className="text-sm">Fast Processing</span>
            </div>
            <div className="flex items-center text-gray-400">
              <Shield size={16} className="mr-1.5 text-purple-400" />
              <span className="text-sm">Secure & Private</span>
            </div>
            <div className="flex items-center text-gray-400">
              <Monitor size={16} className="mr-1.5 text-cyan-400" />
              <span className="text-sm">Browser-Based</span>
            </div>
          </div>
        </section>

        {/* Tools Grid */}
        <section className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 animate-slide-up">
          {tools.map(tool => {
            const colors = colorMap[tool.color];
            const Icon = tool.icon;
            return (
              <div
                key={tool.key}
                onClick={() => handleNavigate(tool.key)}
                className={`bg-slate-800/40 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-700/50 ${colors.border} hover:bg-slate-800/60 transition-all duration-300 cursor-pointer group`}
              >
                <div className={`flex items-center justify-center w-14 h-14 ${colors.bg} rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon size={28} className={colors.text} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{tool.title}</h3>
                <p className="text-gray-400 text-sm mb-4 leading-relaxed">{tool.description}</p>
                <ul className="space-y-1.5">
                  {tool.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-500 text-xs">
                      <div className={`w-1.5 h-1.5 rounded-full ${colors.dot} mr-2`}></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className={`mt-4 flex items-center gap-1 ${colors.text} text-sm font-medium group-hover:gap-2 transition-all`}>
                  Open Tool <ArrowRight size={14} />
                </div>
              </div>
            );
          })}
        </section>

        {/* Why JustAutomateIt */}
        <section className="w-full max-w-3xl bg-slate-800/30 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-slate-700/50 animate-fade-in mb-8">
          <h2 className="text-2xl font-bold text-center mb-6 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-cyan-400">
            Why JustAutomateIt?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              'All image processing happens in your browser — your files never leave your device.',
              'Sign in with Google for a seamless, personalized experience.',
              'Batch process entire folders of images in seconds.',
              'AI-powered product tools for e-commerce sellers and creators.'
            ].map((text, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <div className="mt-1 bg-green-500/20 p-1 rounded-full flex-shrink-0">
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                </div>
                <p className="text-gray-300 text-sm">{text}</p>
              </div>
            ))}
          </div>
        </section>

      </div>
    </>
  );
}

export default HomePage;
